import { useSyncExternalStore } from 'react';
import { Node, Edge } from '@xyflow/react';
import {
  NodeData,
  ActionNode,
  ConditionNode,
  TriggerNode,
  ProcessingNode,
  ApprovalNode,
  CustomNode,
  TekstAppNode,
  UtilityNode,
  ModelNode,
} from 'src/features/automations/[flowId]/components/utils/NodeDataTypes';
import { useFlowStore } from 'src/features/flows/contexts/FlowStoreProvider';
import { FlowActionConfig } from 'src/features/flows/types/flowAction.types';
import { Model } from 'src/shared/types/model.types';

// Types for the store
export type NodePosition = {
  x: number;
  y: number;
};

export type StoreNode = {
  id: string;
  type: string;
  position: NodePosition;
  data: NodeData;
  parentId?: string;
  extent?: 'parent';
  dragging?: boolean;
  selected?: boolean;
  hidden?: boolean;
};

export type StoreEdge = Edge & {
  animated?: boolean;
  highlighted?: boolean;
};

export type FlowMetadata = {
  flowId: string;
  integrations: string[];
  flowActions?: FlowActionConfig[];
  models?: Model[];
  isRunning: boolean;
  version?: number;
};

type BatchUpdate = {
  nodes?: Map<string, Partial<StoreNode>>;
  edges?: Map<string, Partial<StoreEdge>>;
  metadata?: Partial<FlowMetadata>;
};

/**
 * Comprehensive Flow Store
 * Manages all flow state with fine-grained subscriptions for optimal performance.
 * Each component can subscribe to specific data slices to avoid unnecessary re-renders.
 */
class FlowStore {
  // Core data stores
  private nodes = new Map<string, StoreNode>();
  private edges = new Map<string, StoreEdge>();
  private metadata: FlowMetadata = {
    flowId: '',
    integrations: [],
    isRunning: false,
  };
  private hoveredElements = ''; // Comma-separated string of hovered node IDs

  // Subscription management
  private listeners = new Set<() => void>();
  private nodeListeners = new Map<string, Set<() => void>>(); // For complete node changes
  private nodeDataListeners = new Map<string, Set<() => void>>(); // For data-only changes
  private nodePositionListeners = new Map<string, Set<() => void>>(); // For position-only changes
  private edgeListeners = new Map<string, Set<() => void>>();
  private metadataListeners = new Set<() => void>();
  private selectionListeners = new Set<() => void>(); // Selection-only listeners
  private hoverListeners = new Set<() => void>(); // Hover-only listeners
  private edgeTargetListeners = new Map<string, Set<() => void>>(); // Listeners for edges targeting a specific node

  // Batch update state
  private isBatching = false;
  private batchedListeners = new Set<() => void>();
  private batchHadChanges = false; // Track if any changes occurred in batch

  // Track current selection for change detection (now supports multiple selections)
  private selectedNodeIds = new Set<string>();
  private cachedSelectedNodes: StoreNode[] = []; // Cache to prevent infinite loops
  private cachedSelectedIdsString = ''; // Track IDs as string for cache validation
  public flowId: string = '';

  // Subscribe methods
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  subscribeToSelection = (listener: () => void) => {
    this.selectionListeners.add(listener);
    return () => this.selectionListeners.delete(listener);
  };

  subscribeToHover = (listener: () => void) => {
    this.hoverListeners.add(listener);
    return () => this.hoverListeners.delete(listener);
  };

  subscribeToNode = (nodeId: string, listener: () => void) => {
    if (!this.nodeListeners.has(nodeId)) {
      this.nodeListeners.set(nodeId, new Set());
    }
    this.nodeListeners.get(nodeId)!.add(listener);
    return () => {
      this.nodeListeners.get(nodeId)?.delete(listener);
      if (this.nodeListeners.get(nodeId)?.size === 0) {
        this.nodeListeners.delete(nodeId);
      }
    };
  };

  subscribeToNodeData = (nodeId: string | undefined, listener: () => void) => {
    if (!nodeId) return () => {};
    if (!this.nodeDataListeners.has(nodeId)) {
      this.nodeDataListeners.set(nodeId, new Set());
    }
    this.nodeDataListeners.get(nodeId)!.add(listener);
    return () => {
      this.nodeDataListeners.get(nodeId)?.delete(listener);
      if (this.nodeDataListeners.get(nodeId)?.size === 0) {
        this.nodeDataListeners.delete(nodeId);
      }
    };
  };

  subscribeToNodePosition = (nodeId: string, listener: () => void) => {
    if (!this.nodePositionListeners.has(nodeId)) {
      this.nodePositionListeners.set(nodeId, new Set());
    }
    this.nodePositionListeners.get(nodeId)!.add(listener);
    return () => {
      this.nodePositionListeners.get(nodeId)?.delete(listener);
      if (this.nodePositionListeners.get(nodeId)?.size === 0) {
        this.nodePositionListeners.delete(nodeId);
      }
    };
  };

  subscribeToEdge = (edgeId: string, listener: () => void) => {
    if (!this.edgeListeners.has(edgeId)) {
      this.edgeListeners.set(edgeId, new Set());
    }
    this.edgeListeners.get(edgeId)!.add(listener);
    return () => {
      this.edgeListeners.get(edgeId)?.delete(listener);
      if (this.edgeListeners.get(edgeId)?.size === 0) {
        this.edgeListeners.delete(edgeId);
      }
    };
  };

  subscribeToMetadata = (listener: () => void) => {
    this.metadataListeners.add(listener);
    return () => this.metadataListeners.delete(listener);
  };

  subscribeToEdgeTarget = (targetNodeId: string, listener: () => void) => {
    if (!this.edgeTargetListeners.has(targetNodeId)) {
      this.edgeTargetListeners.set(targetNodeId, new Set());
    }
    this.edgeTargetListeners.get(targetNodeId)!.add(listener);
    return () => {
      this.edgeTargetListeners.get(targetNodeId)?.delete(listener);
      if (this.edgeTargetListeners.get(targetNodeId)?.size === 0) {
        this.edgeTargetListeners.delete(targetNodeId);
      }
    };
  };

  // Snapshot methods for useSyncExternalStore
  getSnapshot = () => ({
    nodes: this.nodes,
    edges: this.edges,
    metadata: this.metadata,
  });

  getNode = (nodeId: string): StoreNode | undefined => {
    return this.nodes.get(nodeId);
  };

  getNodeData = (nodeId: string | undefined): NodeData | undefined => {
    if (!nodeId) return undefined;
    const node = this.nodes.get(nodeId);
    return node?.data;
  };

  getNodePosition = (nodeId: string): NodePosition | undefined => {
    const node = this.nodes.get(nodeId);
    return node ? { x: node.position.x, y: node.position.y } : undefined;
  };

  getEdge = (edgeId: string): StoreEdge | undefined => {
    return this.edges.get(edgeId);
  };

  getMetadata = (): FlowMetadata => {
    return { ...this.metadata };
  };

  getAllNodes = (): StoreNode[] => {
    return Array.from(this.nodes.values());
  };

  getAllEdges = (): StoreEdge[] => {
    return Array.from(this.edges.values());
  };

  getHoveredElements = (): string => {
    return this.hoveredElements;
  };

  getSelectedNodeIds = (): string[] => {
    return Array.from(this.selectedNodeIds);
  };

  getSelectedNodes = (): StoreNode[] => {
    // Create a stable string representation of selected IDs for comparison
    const currentIdsString = Array.from(this.selectedNodeIds).sort().join(',');

    // If no nodes selected, return empty array (and cache it)
    if (this.selectedNodeIds.size === 0) {
      if (this.cachedSelectedIdsString !== '') {
        this.cachedSelectedNodes = [];
        this.cachedSelectedIdsString = '';
      }
      return this.cachedSelectedNodes;
    }

    // Check if cache is still valid by comparing the IDs string
    if (this.cachedSelectedIdsString === currentIdsString && this.cachedSelectedNodes.length > 0) {
      // Verify all cached nodes still exist and are selected
      const allNodesValid = this.cachedSelectedNodes.every((node) => this.nodes.has(node.id) && node.selected);
      if (allNodesValid) {
        return this.cachedSelectedNodes;
      }
    }

    // Rebuild cache - get nodes in sorted order for consistency
    const sortedIds = Array.from(this.selectedNodeIds).sort();
    this.cachedSelectedNodes = sortedIds
      .map((id) => this.nodes.get(id))
      .filter((node): node is StoreNode => node !== undefined && node.selected === true);
    this.cachedSelectedIdsString = currentIdsString;

    return this.cachedSelectedNodes;
  };

  clearSelection = () => {
    // Deselect all currently selected nodes
    const nodesToDeselect = Array.from(this.selectedNodeIds);
    this.selectedNodeIds.clear();
    this.cachedSelectedNodes = []; // Clear cache

    // Update node states
    nodesToDeselect.forEach((nodeId) => {
      const node = this.nodes.get(nodeId);
      if (node) {
        this.nodes.set(nodeId, { ...node, selected: false });
        this.nodeListeners.get(nodeId)?.forEach((listener) => listener());
      }
    });

    // Notify listeners
    if (nodesToDeselect.length > 0) {
      this.selectionListeners.forEach((listener) => listener());
      this.listeners.forEach((listener) => listener());
    }
  };

  // Update methods
  updateNode = (nodeId: string, updates: Partial<StoreNode>) => {
    const existing = this.nodes.get(nodeId);
    if (existing) {
      // Check if anything actually changed to prevent unnecessary updates
      let hasChanges = false;
      const changedKeys: string[] = [];
      for (const [key, value] of Object.entries(updates)) {
        if ((existing as any)[key] !== value) {
          hasChanges = true;
          changedKeys.push(key);
        }
      }

      if (!hasChanges) {
        // Don't notify anyone if nothing changed!
        return;
      }

      // Check if selection changed
      const selectionChanged = 'selected' in updates && existing.selected !== updates.selected;
      if (selectionChanged) {
        // Update tracked selection (now supports multiple selections)
        if (updates.selected) {
          // Add to selection set
          this.selectedNodeIds.add(nodeId);
        } else {
          // Remove from selection set
          this.selectedNodeIds.delete(nodeId);
        }
        // Invalidate cached selected nodes - will be recalculated on next access
        this.cachedSelectedNodes = [];
        this.cachedSelectedIdsString = '';
      }

      this.nodes.set(nodeId, { ...existing, ...updates });

      // Determine what types of changes occurred
      const hasDataChanges = 'data' in updates;
      const hasPositionChanges = 'position' in updates;
      const isLocalChange =
        changedKeys.length === 1 && (changedKeys[0].startsWith('dragging:') || changedKeys[0].startsWith('position:'));

      // If batching, collect listeners for later notification
      if (this.isBatching) {
        // Mark that we had actual changes
        this.batchHadChanges = true;

        // Notify based on what changed
        if (hasDataChanges) {
          // Data changed - notify data listeners
          this.nodeDataListeners.get(nodeId)?.forEach((listener) => this.batchedListeners.add(listener));
        }

        if (hasPositionChanges) {
          // Position changed - notify position listeners
          this.nodePositionListeners.get(nodeId)?.forEach((listener) => this.batchedListeners.add(listener));
        }

        // Always notify complete node listeners (they want everything)
        this.nodeListeners.get(nodeId)?.forEach((listener) => this.batchedListeners.add(listener));

        // Only notify global listeners for non-local changes
        if (!isLocalChange) {
          this.listeners.forEach((listener) => this.batchedListeners.add(listener));
        }

        // Only add selection listeners if selection actually changed
        if (selectionChanged) {
          this.selectionListeners.forEach((listener) => this.batchedListeners.add(listener));
        }
      } else {
        // Not batching - notify immediately based on what changed
        if (hasDataChanges) {
          this.nodeDataListeners.get(nodeId)?.forEach((listener) => listener());
        }

        if (hasPositionChanges) {
          this.nodePositionListeners.get(nodeId)?.forEach((listener) => listener());
        }

        // Always notify complete node listeners
        this.nodeListeners.get(nodeId)?.forEach((listener) => listener());

        // Only notify global listeners for non-local changes
        if (!isLocalChange) {
          this.listeners.forEach((listener) => listener());
        }

        // Notify selection listeners only if selection changed
        if (selectionChanged) {
          this.selectionListeners.forEach((listener) => listener());
        }
      }
    }
  };

  updateNodeData = (nodeId: string, data: Partial<NodeData>) => {
    const existing = this.nodes.get(nodeId);
    if (existing) {
      this.nodes.set(nodeId, {
        ...existing,
        data: { ...existing.data, ...data } as NodeData,
      });

      // If batching, collect listeners for later notification
      if (this.isBatching) {
        // Mark that we had actual changes
        this.batchHadChanges = true;

        // Notify data-specific listeners and complete node listeners
        this.nodeDataListeners.get(nodeId)?.forEach((listener) => this.batchedListeners.add(listener));
        this.nodeListeners.get(nodeId)?.forEach((listener) => this.batchedListeners.add(listener));
        this.listeners.forEach((listener) => this.batchedListeners.add(listener));
      } else {
        // Notify data-specific listeners and complete node listeners
        this.nodeDataListeners.get(nodeId)?.forEach((listener) => listener());
        this.nodeListeners.get(nodeId)?.forEach((listener) => listener());
        this.listeners.forEach((listener) => listener());
      }
    }
  };

  updateNodePosition = (nodeId: string, position: NodePosition) => {
    const existing = this.nodes.get(nodeId);
    if (existing) {
      // Check if position actually changed to prevent unnecessary updates
      if (existing.position.x === position.x && existing.position.y === position.y) {
        return;
      }

      this.nodes.set(nodeId, { ...existing, position });

      // Position changes should only notify position-specific and complete node listeners
      // NOT data listeners or global listeners
      if (this.isBatching) {
        // Mark that we had actual changes
        this.batchHadChanges = true;

        // Only notify position-specific listeners and complete node listeners
        this.nodePositionListeners.get(nodeId)?.forEach((listener) => this.batchedListeners.add(listener));
        this.nodeListeners.get(nodeId)?.forEach((listener) => this.batchedListeners.add(listener));
        // Don't notify data listeners or global listeners - they don't care about position!
      } else {
        // Only notify position-specific listeners and complete node listeners
        this.nodePositionListeners.get(nodeId)?.forEach((listener) => listener());
        this.nodeListeners.get(nodeId)?.forEach((listener) => listener());
      }
    }
  };

  setNodes = (nodes: StoreNode[]) => {
    this.nodes.clear();
    nodes.forEach((node) => {
      this.nodes.set(node.id, node);
    });
    this.listeners.forEach((listener) => listener());
  };

  setEdges = (edges: StoreEdge[]) => {
    this.edges.clear();
    edges.forEach((edge) => {
      this.edges.set(edge.id, edge);
    });
    this.listeners.forEach((listener) => listener());
  };

  addNode = (node: StoreNode) => {
    console.log(`[FlowStore] addNode(${node.id}) - Adding node with selected: ${node.selected}`);

    // Handle selection tracking if node is being added as selected (now supports multiple selections)
    if (node.selected) {
      this.selectedNodeIds.add(node.id);
      // Invalidate cached selected nodes - will be recalculated on next access
      this.cachedSelectedNodes = [];
    }

    this.nodes.set(node.id, node);
    this.nodeListeners.get(node.id)?.forEach((listener) => listener());
    this.listeners.forEach((listener) => listener());

    // Notify selection listeners if node was added as selected
    if (node.selected) {
      console.log(`[FlowStore] addNode - Notifying selection listeners for: ${node.id}`);
      this.selectionListeners.forEach((listener) => listener());
    }
  };

  removeNode = (nodeId: string) => {
    this.nodes.delete(nodeId);
    // Remove from selection if it was selected
    const wasSelected = this.selectedNodeIds.delete(nodeId);
    if (wasSelected) {
      // Invalidate cached selected nodes - will be recalculated on next access
      this.cachedSelectedNodes = [];
    }
    // Also remove any edges connected to this node
    this.edges.forEach((edge, edgeId) => {
      if (edge.source === nodeId || edge.target === nodeId) {
        this.edges.delete(edgeId);
        this.edgeListeners.get(edgeId)?.forEach((listener) => listener());
      }
    });
    this.nodeListeners.get(nodeId)?.forEach((listener) => listener());
    this.listeners.forEach((listener) => listener());
    // Notify selection listeners since selection may have changed
    if (wasSelected) {
      this.selectionListeners.forEach((listener) => listener());
    }
  };

  updateEdge = (edgeId: string, updates: Partial<StoreEdge>) => {
    const existing = this.edges.get(edgeId);
    if (existing) {
      this.edges.set(edgeId, { ...existing, ...updates });
      this.edgeListeners.get(edgeId)?.forEach((listener) => listener());
      this.listeners.forEach((listener) => listener());
    }
  };

  addEdge = (edge: StoreEdge) => {
    this.edges.set(edge.id, edge);
    this.edgeListeners.get(edge.id)?.forEach((listener) => listener());
    // Notify listeners for edges targeting this edge's target node
    this.edgeTargetListeners.get(edge.target)?.forEach((listener) => listener());
    this.listeners.forEach((listener) => listener());
  };

  removeEdge = (edgeId: string) => {
    const edge = this.edges.get(edgeId);
    this.edges.delete(edgeId);
    this.edgeListeners.get(edgeId)?.forEach((listener) => listener());
    // Notify listeners for edges targeting this edge's target node
    if (edge) {
      this.edgeTargetListeners.get(edge.target)?.forEach((listener) => listener());
    }
    this.listeners.forEach((listener) => listener());
  };

  updateMetadata = (updates: Partial<FlowMetadata>) => {
    this.metadata = { ...this.metadata, ...updates };
    this.metadataListeners.forEach((listener) => listener());
    this.listeners.forEach((listener) => listener());
  };

  setHoveredElements = (nodeIds: string) => {
    if (this.hoveredElements !== nodeIds) {
      this.hoveredElements = nodeIds;
      this.hoverListeners.forEach((listener) => listener());
    }
  };

  addHoveredElement = (nodeId: string) => {
    const currentIds = this.hoveredElements ? this.hoveredElements.split(',').filter((id) => id !== '') : [];
    if (!currentIds.includes(nodeId)) {
      const newIds = [...currentIds, nodeId].join(',');
      this.setHoveredElements(newIds);
    }
  };

  removeHoveredElement = (nodeId: string) => {
    const currentIds = this.hoveredElements ? this.hoveredElements.split(',').filter((id) => id !== '') : [];
    const newIds = currentIds.filter((id) => id !== nodeId).join(',');
    this.setHoveredElements(newIds);
  };

  // Batch update for performance
  batchUpdate = (updates: BatchUpdate | (() => void)) => {
    // Handle callback-style batch update
    if (typeof updates === 'function') {
      // Set batching flag and reset change tracking
      this.isBatching = true;
      this.batchHadChanges = false;
      this.batchedListeners.clear();

      // Execute the updates
      updates();

      // Reset batching flag
      this.isBatching = false;

      // Only notify if there were actual changes AND listeners to notify
      if (this.batchHadChanges && this.batchedListeners.size > 0) {
        // Notify all collected listeners once
        this.batchedListeners.forEach((listener) => listener());
      }

      this.batchedListeners.clear();
      this.batchHadChanges = false; // Reset for next batch
      return;
    }

    // Original object-style batch update
    // Update nodes
    if (updates.nodes) {
      updates.nodes.forEach((nodeUpdates, nodeId) => {
        const existing = this.nodes.get(nodeId);
        if (existing) {
          this.nodes.set(nodeId, { ...existing, ...nodeUpdates });
        }
      });
    }

    // Update edges
    if (updates.edges) {
      updates.edges.forEach((edgeUpdates, edgeId) => {
        const existing = this.edges.get(edgeId);
        if (existing) {
          this.edges.set(edgeId, { ...existing, ...edgeUpdates });
        }
      });
    }

    // Update metadata
    if (updates.metadata) {
      this.metadata = { ...this.metadata, ...updates.metadata };
    }

    // Notify all relevant listeners once
    if (updates.nodes) {
      updates.nodes.forEach((_, nodeId) => {
        this.nodeListeners.get(nodeId)?.forEach((listener) => listener());
      });
    }
    if (updates.edges) {
      updates.edges.forEach((_, edgeId) => {
        this.edgeListeners.get(edgeId)?.forEach((listener) => listener());
      });
    }
    if (updates.metadata) {
      this.metadataListeners.forEach((listener) => listener());
    }
    this.listeners.forEach((listener) => listener());
  };

  // Initialize from existing flow
  initializeFromReactFlow = (nodes: Node<NodeData>[], edges: Edge[], metadata: Partial<FlowMetadata>) => {
    this.flowId = metadata.flowId || '';
    console.log('🚀 [FlowStore] Initializing with', nodes.length, 'nodes and', edges.length, 'edges');

    // Clear existing data
    this.nodes.clear();
    this.edges.clear();

    // Reset selection tracking (now supports multiple selections)
    this.selectedNodeIds.clear();
    this.cachedSelectedNodes = [];
    this.cachedSelectedIdsString = '';

    // Add all nodes and track initial selection
    nodes.forEach((node) => {
      console.log('📦 [FlowStore] Adding node:', node.id, 'with data:', node.data);
      this.nodes.set(node.id, {
        id: node.id,
        type: node.type || 'default',
        position: node.position,
        data: node.data,
        parentId: node.parentId,
        extent: node.extent,
        dragging: node.dragging,
        selected: node.selected,
        hidden: node.hidden,
      } as StoreNode);

      // Track selected nodes (now supports multiple selections)
      if (node.selected) {
        this.selectedNodeIds.add(node.id);
      }
    });

    // Rebuild cache after all nodes are added
    this.cachedSelectedNodes = Array.from(this.selectedNodeIds)
      .map((id) => this.nodes.get(id))
      .filter((node): node is StoreNode => node !== undefined);

    // Add all edges
    edges.forEach((edge) => {
      this.edges.set(edge.id, edge as StoreEdge);
    });

    // Update metadata
    this.metadata = { ...this.metadata, ...metadata };

    console.log('✅ [FlowStore] Initialization complete. Store has', this.nodes.size, 'nodes');

    // Notify all listeners
    this.listeners.forEach((listener) => listener());
    this.metadataListeners.forEach((listener) => listener());
    this.selectionListeners.forEach((listener) => listener());
  };

  // Convert to ReactFlow format for compatibility
  toReactFlowNodes = (): Node<NodeData>[] => {
    return Array.from(this.nodes.values()).map((node) => ({
      id: node.id,
      type: node.type,
      position: node.position,
      data: node.data,
      parentId: node.parentId,
      extent: node.extent,
      dragging: node.dragging,
      selected: node.selected,
      hidden: node.hidden,
    }));
  };

  toReactFlowEdges = (): Edge[] => {
    return Array.from(this.edges.values());
  };

  // Clear all data
  // Simulation-specific methods (backward compatibility)
  handleBlockRun = (
    blockId: string,
    hasError: boolean,
    blockData?: {
      block_type?: string;
      runtime?: {
        status?: string;
        skipped?: string;
        duration?: number | { lockWait: number; totalTime: number; actionTime: number };
      };
    }
  ) => {
    const node = this.nodes.get(blockId);
    if (!node) return;

    const hasDuration = blockData?.runtime?.duration !== undefined;
    const isSkipped = !!blockData?.runtime?.skipped;
    const isFinished = hasDuration || isSkipped;
    const isRunnable = ['action', 'app'].includes(blockData?.block_type);

    let simulateState: NodeData['simulateState'];
    let highlightedInFlowRun = true;

    if (!isRunnable) {
      simulateState = 'completed';
    } else if (isFinished) {
      if (isSkipped) {
        simulateState = 'skipped';
      } else if (
        blockData?.block_type === 'approval' &&
        ['pending', 'failed'].includes(blockData.runtime?.status || '')
      ) {
        simulateState = 'approval';
      } else if (hasError) {
        simulateState = 'error';
      } else {
        simulateState = 'completed';
      }
    } else {
      simulateState = 'running';
    }

    this.updateNodeData(blockId, {
      simulateState,
      highlightedInFlowRun,
    });
  };

  handleBlockPending = (blockId: string) => {
    const node = this.nodes.get(blockId);
    if (!node) return;

    const currentState = node.data.simulateState;
    if (currentState && ['completed', 'error', 'skipped', 'approval'].includes(currentState)) {
      return;
    }

    this.updateNodeData(blockId, {
      simulateState: 'running',
      highlightedInFlowRun: false,
    });
  };
}

// Export the FlowStore class for use in FlowStoreProvider
export { FlowStore };

// ===== HOOKS FOR ACCESSING THE STORE =====

/**
 * Hook to subscribe to a specific node's complete data
 *
 * @template T - The specific NodeData type expected (e.g., ActionNode, ConditionNode)
 * @param nodeId - The ID of the node to subscribe to
 * @param typeGuard - Optional type guard function to verify the node data type at runtime
 * @returns The node data typed as T, or undefined if not found or type guard fails
 *
 * @example
 * // With generic only (compile-time type safety)
 * const actionData = useNodeData<ActionNode>(nodeId);
 *
 * @example
 * // With type guard (runtime type safety)
 * const actionData = useNodeData<ActionNode>(
 *   nodeId,
 *   (data): data is ActionNode => data.type === 'action'
 * );
 */
export function useNodeData<T extends NodeData = NodeData>(
  nodeId: string | undefined,
  typeGuard?: (data: NodeData) => data is T
): T | undefined {
  const flowStore = useFlowStore();
  const data = useSyncExternalStore(
    (listener) => flowStore.subscribeToNodeData(nodeId, listener), // Use data-specific subscription
    () => flowStore.getNodeData(nodeId),
    () => flowStore.getNodeData(nodeId)
  );

  // If no type guard provided, just cast and return
  if (!typeGuard) {
    return data as T | undefined;
  }

  // If type guard provided, verify the type at runtime
  if (data && typeGuard(data)) {
    return data;
  }

  return undefined;
}

// ===== TYPE GUARD HELPERS =====
// Convenient type guards to use with useNodeData
// Each guard is kept pure and declarative of a single type

export const isActionNode = (data: NodeData): data is ActionNode => data.type === 'action';
export const isConditionNode = (data: NodeData): data is ConditionNode => data.type === 'condition';
export const isTriggerNode = (data: NodeData): data is TriggerNode => data.type === 'trigger';
export const isProcessingNode = (data: NodeData): data is ProcessingNode => data.type === 'tekst';
export const isApprovalNode = (data: NodeData): data is ApprovalNode => data.type === 'approval';
export const isCustomNode = (data: NodeData): data is CustomNode => data.type === 'custom';
export const isAppNode = (data: NodeData): data is TekstAppNode => data.type === 'app';
export const isUtilityNode = (data: NodeData): data is UtilityNode => data.type === 'utility';
export const isModelNode = (data: NodeData): data is ModelNode => data.type === 'model';

/**
 * Hook to subscribe to a specific node's position only
 */
export function useNodePosition(nodeId: string): NodePosition | undefined {
  const flowStore = useFlowStore();
  return useSyncExternalStore(
    (listener) => flowStore.subscribeToNodePosition(nodeId, listener), // Use position-specific subscription
    () => flowStore.getNodePosition(nodeId),
    () => flowStore.getNodePosition(nodeId)
  );
}

/**
 * Hook to subscribe to a complete node
 */
export function useNode(nodeId: string): StoreNode | undefined {
  const flowStore = useFlowStore();
  return useSyncExternalStore(
    (listener) => flowStore.subscribeToNode(nodeId, listener),
    () => flowStore.getNode(nodeId),
    () => flowStore.getNode(nodeId)
  );
}

/**
 * Hook to subscribe to a specific edge
 */
export function useEdgeData(edgeId: string): StoreEdge | undefined {
  const flowStore = useFlowStore();
  return useSyncExternalStore(
    (listener) => flowStore.subscribeToEdge(edgeId, listener),
    () => flowStore.getEdge(edgeId),
    () => flowStore.getEdge(edgeId)
  );
}

/**
 * Hook to subscribe to flow metadata
 */
export function useFlowMetadata(): FlowMetadata {
  const flowStore = useFlowStore();
  return useSyncExternalStore(
    flowStore.subscribeToMetadata,
    () => flowStore.getMetadata(),
    () => flowStore.getMetadata()
  );
}

/**
 * Hook to get all nodes (subscribes to any node changes)
 */
export function useAllNodes(): StoreNode[] {
  const flowStore = useFlowStore();
  return useSyncExternalStore(
    flowStore.subscribe,
    () => flowStore.getAllNodes(),
    () => flowStore.getAllNodes()
  );
}

/**
 * Hook to get all edges (subscribes to any edge changes)
 */
export function useAllEdges(): StoreEdge[] {
  const flowStore = useFlowStore();
  return useSyncExternalStore(
    flowStore.subscribe,
    () => flowStore.getAllEdges(),
    () => flowStore.getAllEdges()
  );
}

// Note: These functions are now created per hook call since they need access to the store instance

/**
 * Hook to get all selected nodes (supports multiselect)
 *
 * @template T - The specific NodeData type expected (e.g., ActionNode, ConditionNode)
 * @param typeGuard - Optional type guard function to filter nodes by type
 * @returns Array of selected nodes with data typed as T
 *
 * @example
 * // Get all selected nodes
 * const selectedNodes = useSelectedNodes();
 *
 * @example
 * // Get only selected ActionNodes
 * const actionNodes = useSelectedNodes<ActionNode>(
 *   (data): data is ActionNode => data.type === 'action'
 * );
 */
export function useSelectedNodes<T extends NodeData = NodeData>(
  typeGuard?: (data: NodeData) => data is T
): (StoreNode & { data: T })[] {
  const flowStore = useFlowStore();
  const selectedNodes = useSyncExternalStore(
    (listener) => flowStore.subscribeToSelection(listener),
    () => flowStore.getSelectedNodes(),
    () => flowStore.getSelectedNodes()
  );

  // If no type guard provided, return all selected nodes
  if (!typeGuard) {
    return selectedNodes as (StoreNode & { data: T })[];
  }

  // If type guard provided, filter by type
  return selectedNodes.filter((node) => typeGuard(node.data)) as (StoreNode & { data: T })[];
}

/**
 * Hook to get only the first selected node (backward compatibility)
 * For multiselect, use useSelectedNodes() instead
 *
 * @template T - The specific NodeData type expected (e.g., ActionNode, ConditionNode)
 * @param typeGuard - Optional type guard function to verify the node data type at runtime
 * @returns The first selected node with data typed as T, or undefined if not found or type guard fails
 *
 * @example
 * // Without type guard (returns first selected node)
 * const selectedNode = useSelectedNode();
 *
 * @example
 * // With type guard (only returns if first selected node is an ActionNode)
 * const actionNode = useSelectedNode<ActionNode>(
 *   (data): data is ActionNode => data.type === 'action'
 * );
 */
export function useSelectedNode<T extends NodeData = NodeData>(
  typeGuard?: (data: NodeData) => data is T
): (StoreNode & { data: T }) | undefined {
  const flowStore = useFlowStore();
  const selectedNodes = useSyncExternalStore(
    (listener) => flowStore.subscribeToSelection(listener),
    () => flowStore.getSelectedNodes(),
    () => flowStore.getSelectedNodes()
  );

  // Get first selected node
  const selectedNode = selectedNodes[0];

  // If no node selected, return undefined
  if (!selectedNode) {
    return undefined;
  }

  // If no type guard provided, just cast and return
  if (!typeGuard) {
    return selectedNode as StoreNode & { data: T };
  }

  // If type guard provided, verify the type at runtime
  if (typeGuard(selectedNode.data)) {
    return selectedNode as StoreNode & { data: T };
  }

  return undefined;
}

/**
 * Hook to check if a specific node is hovered
 */
export function useIsHoveredNode(nodeId: string): boolean {
  const flowStore = useFlowStore();
  const hoveredElements = useSyncExternalStore(
    flowStore.subscribeToHover,
    () => {
      const hovered = flowStore.getHoveredElements();
      if (!hovered) return false;
      return hovered.split(',').includes(nodeId);
    },
    () => {
      const hovered = flowStore.getHoveredElements();
      if (!hovered) return false;
      return hovered.split(',').includes(nodeId);
    }
  );

  return hoveredElements;
}

/**
 * Hook to check if a specific node is selected
 * Optimized to only trigger rerenders when THIS node's selection state changes
 * The snapshot function returns a boolean directly, so React only rerenders when the boolean changes
 * Now supports multiselect - returns true if the node is in the selection set
 */
export function useIsSelectedNode(nodeId: string): boolean {
  const flowStore = useFlowStore();
  // Use a selector that computes the boolean directly in the snapshot
  // This ensures React only rerenders when the boolean value actually changes for THIS node
  // Note: getAllNodes() creates a new array, but React compares the boolean result, not the array
  return useSyncExternalStore(
    flowStore.subscribeToSelection,
    () => {
      const nodes = flowStore.getAllNodes();
      const selectedNode = nodes.find((node) => node.selected && node.id === nodeId);
      return !!selectedNode;
    },
    () => {
      const nodes = flowStore.getAllNodes();
      const selectedNode = nodes.find((node) => node.selected && node.id === nodeId);
      return !!selectedNode;
    }
  );
}
